import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAlert } from 'react-alert';
import {
  FaFileCode, FaCopy, FaDownload, FaSyncAlt,
  FaChevronRight, FaChevronDown, FaExclamationTriangle,
  FaEye, FaTimes, FaTable, FaSpinner,
} from 'react-icons/fa';
import AppLayout from '../layout/AppLayout';
import Loader from '../loader/loader';
import './sampleJsonViewer.css';

const SAMPLE_API_BASE = 'https://rnxsohimg1.execute-api.ap-south-1.amazonaws.com/Production';
const MAX_COUNT = 10;
const MIN_COUNT = 1;

// ─── Collapsible JSON tree ────────────────────────────────────────────────────
const JsonNode = ({ name, value, depth = 0, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const isArray = Array.isArray(value);
  const isObject = value !== null && typeof value === 'object' && !isArray;

  if (!isObject && !isArray) {
    const displayValue =
      value === null ? 'null'
        : typeof value === 'string' ? `"${value}"`
          : String(value);
    const valueClass =
      typeof value === 'string' ? 'sjv-json-string'
        : typeof value === 'number' ? 'sjv-json-number'
          : typeof value === 'boolean' ? 'sjv-json-bool'
            : 'sjv-json-null';
    return (
      <div className="sjv-tree-leaf" style={{ paddingLeft: depth * 16 + 8 }}>
        {name != null && <span className="sjv-json-key">"{name}"</span>}
        {name != null && <span className="sjv-tree-colon">: </span>}
        <span className={valueClass}>{displayValue}</span>
      </div>
    );
  }

  const keys = isArray ? value.length : Object.keys(value).length;
  const label = isArray ? `[ ${keys} items ]` : `{ ${keys} keys }`;

  return (
    <div className="sjv-tree-node">
      <button
        type="button"
        className="sjv-tree-toggle"
        onClick={() => setIsOpen(!isOpen)}
        style={{ paddingLeft: depth * 16 + 8 }}
        aria-expanded={isOpen}
      >
        <span className="sjv-tree-icon">{isOpen ? <FaChevronDown /> : <FaChevronRight />}</span>
        {name != null && <span className="sjv-json-key">"{name}"</span>}
        {name != null && <span className="sjv-tree-colon">: </span>}
        <span className="sjv-tree-bracket">{label}</span>
      </button>
      {isOpen && (
        <div className="sjv-tree-children">
          {isArray
            ? value.map((item, i) => (
              <JsonNode key={i} name={i} value={item} depth={depth + 1} defaultOpen={depth < 1} />
            ))
            : Object.entries(value).map(([k, v]) => (
              <JsonNode key={k} name={k} value={v} depth={depth + 1} defaultOpen={depth < 1} />
            ))}
        </div>
      )}
    </div>
  );
};

const JsonTree = ({ data, defaultOpen = false }) => (
  <div className="sjv-json-tree">
    <JsonNode value={data} defaultOpen={defaultOpen} />
  </div>
);

// ─── Helper ───────────────────────────────────────────────────────────────────
const getDisplayPayload = (data) => {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return data;
  const keys = Object.keys(data);
  if (keys.length === 1) return data[keys[0]];
  return data;
};

const getFilename = (url) => {
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    return parts[parts.length - 1] || url;
  } catch {
    return url;
  }
};

const truncateUrl = (url, maxLen = 60) => {
  if (!url || url.length <= maxLen) return url;
  return url.slice(0, maxLen) + '…';
};

// ─── JSON Viewer Modal ────────────────────────────────────────────────────────
const JsonViewerModal = ({ item, index, onClose, onCopy, onDownload, copySuccess }) => {
  const overlayRef = useRef(null);
  const closeRef = useRef(null);

  // Focus the close button on open; restore focus on close
  useEffect(() => {
    const prev = document.activeElement;
    closeRef.current?.focus();
    return () => prev?.focus();
  }, []);

  // Escape key closes the modal
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const displayPayload = item.json ? getDisplayPayload(item.json) : null;

  return (
    <div
      className="sjv-modal-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={`Sample ${index + 1} JSON viewer`}
    >
      <div className="sjv-modal">
        {/* Header */}
        <div className="sjv-modal-header">
          <div className="sjv-modal-title-group">
            <FaFileCode className="sjv-modal-title-icon" />
            <div>
              <span className="sjv-modal-title">Sample {index + 1}</span>
              {item.s3Url && (
                <span className="sjv-modal-subtitle" title={item.s3Url}>
                  {getFilename(item.s3Url)}
                </span>
              )}
            </div>
          </div>
          <div className="sjv-modal-actions">
            {!item.error && !item.loading && (
              <>
                <button
                  type="button"
                  className="sjv-btn-icon"
                  onClick={() => onCopy(displayPayload, index)}
                  title="Copy JSON"
                >
                  <FaCopy /> {copySuccess === index ? 'Copied!' : 'Copy'}
                </button>
                <button
                  type="button"
                  className="sjv-btn-icon"
                  onClick={() => onDownload(displayPayload, index)}
                  title="Download JSON"
                >
                  <FaDownload /> Download
                </button>
              </>
            )}
            <button
              ref={closeRef}
              type="button"
              className="sjv-modal-close"
              onClick={onClose}
              title="Close"
              aria-label="Close JSON viewer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="sjv-modal-body">
          {item.loading && (
            <div className="sjv-modal-loading">
              <FaSpinner className="sjv-modal-spinner" />
              <span>Fetching JSON from S3…</span>
            </div>
          )}
          {!item.loading && item.error && (
            <div className="sjv-modal-error">
              <FaExclamationTriangle />
              <span>{item.error}</span>
            </div>
          )}
          {!item.loading && !item.error && displayPayload != null && (
            <JsonTree data={displayPayload} defaultOpen={true} />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SampleJsonViewer = () => {
  const [count, setCount] = useState(1);
  const [org, setOrg] = useState('');
  const [center, setCenter] = useState('');

  // urlRows: [{ s3Url, filename }] — populated after Type-A call
  const [urlRows, setUrlRows] = useState([]);
  // jsonCache: { [s3Url]: { json, error } } — populated lazily on demand
  const [jsonCache, setJsonCache] = useState({});
  // loadingUrls: Set of s3Urls currently being fetched
  const [loadingUrls, setLoadingUrls] = useState(new Set());

  const [globalLoading, setGlobalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [copySuccess, setCopySuccess] = useState(null);

  // Modal state
  const [modalIndex, setModalIndex] = useState(null); // index into urlRows, or null

  const resultsRef = useRef(null);
  const alert = useAlert();

  // Pre-fill org/center — admin selected client takes priority over own credentials
  useEffect(() => {
    let resolvedOrg = '';
    let resolvedCenter = '';

    // 1st priority: admin has a client selected in sessionStorage
    try {
      const adminClient = sessionStorage.getItem('adminSelectedClient');
      if (adminClient) {
        const parsed = JSON.parse(adminClient);
        resolvedOrg = parsed?.org || '';
        resolvedCenter = parsed?.center || '';
      }
    } catch (e) {
      console.warn('Failed to read adminSelectedClient:', e);
    }

    // 2nd priority: fallback to own localStorage credentials (regular client login)
    if (!resolvedOrg || !resolvedCenter) {
      try {
        const creds = localStorage.getItem('credentials');
        if (creds) {
          const parsed = JSON.parse(creds);
          if (parsed.org) resolvedOrg = parsed.org;
          if (parsed.center) resolvedCenter = parsed.center;
        }
      } catch (e) {
        console.error('Failed to parse credentials:', e);
      }
    }

    if (resolvedOrg) setOrg(resolvedOrg);
    if (resolvedCenter) setCenter(resolvedCenter);
  }, []);

  // ── Step 1: Type-A call — fetch URL list only ─────────────────────────────
  const fetchSamples = async () => {
    const effectiveOrg = org?.trim() || 'rajagiri';
    const effectiveCenter = center?.trim() || 'rajagiri';
    let effectiveReportType = 'dynamic';
    try {
      const creds = localStorage.getItem('credentials');
      if (creds) {
        const parsed = JSON.parse(creds);
        if (parsed.reportSubscription?.reportType) {
          effectiveReportType = parsed.reportSubscription.reportType;
        }
      }
    } catch (_) { }
    const effectiveCount = Math.min(MAX_COUNT, Math.max(MIN_COUNT, Number(count) || 1));

    setGlobalLoading(true);
    setError(null);
    setUrlRows([]);
    setJsonCache({});
    setLoadingUrls(new Set());
    setHasFetched(false);
    setModalIndex(null);

    try {
      const sampleUrl = `${SAMPLE_API_BASE}/clientdashboard/sample?org=${encodeURIComponent(effectiveOrg)}&center=${encodeURIComponent(effectiveCenter)}&report_type=${encodeURIComponent(effectiveReportType)}&count=${effectiveCount}`;
      const response = await axios.get(sampleUrl);

      const urlList = Array.isArray(response.data) ? response.data : [];
      const withUrls = urlList.filter((item) => item && item.url);

      if (withUrls.length === 0) {
        setError('No sample URLs returned. Try a different org or center.');
        setHasFetched(true);
        setGlobalLoading(false);
        return;
      }

      // Build rows from URL list — no JSON fetched yet
      const rows = withUrls.map((item) => ({
        s3Url: item.url,
        filename: getFilename(item.url),
      }));

      setUrlRows(rows);
      setHasFetched(true);
      setGlobalLoading(false);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || 'Failed to fetch sample URLs.';
      setError(errMsg);
      setHasFetched(true);
      setGlobalLoading(false);
    }
  };

  // ── Step 2: Lazy-fetch JSON for one row (with caching) ────────────────────
  const fetchJsonForRow = useCallback(async (s3Url) => {
    // Already cached — nothing to do
    if (jsonCache[s3Url]) return;
    // Already in-flight — nothing to do
    if (loadingUrls.has(s3Url)) return;

    setLoadingUrls((prev) => new Set([...prev, s3Url]));
    try {
      const jsonUrl = `${SAMPLE_API_BASE}/inputjson/s3json?s3url=${encodeURIComponent(s3Url)}`;
      const jsonResponse = await axios.get(jsonUrl);
      setJsonCache((prev) => ({ ...prev, [s3Url]: { json: jsonResponse.data, error: null } }));
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || 'Failed to fetch JSON';
      setJsonCache((prev) => ({ ...prev, [s3Url]: { json: null, error: errMsg } }));
    } finally {
      setLoadingUrls((prev) => {
        const next = new Set(prev);
        next.delete(s3Url);
        return next;
      });
    }
  }, [jsonCache, loadingUrls]);

  // Opens the modal and triggers the lazy fetch if needed
  const handleViewJson = (index) => {
    const row = urlRows[index];
    setModalIndex(index);
    fetchJsonForRow(row.s3Url);
  };

  const handleCloseModal = () => setModalIndex(null);

  // ── Copy / Download ───────────────────────────────────────────────────────
  const handleCopy = async (json, index) => {
    const text = JSON.stringify(json ?? null, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(index);
      setTimeout(() => setCopySuccess(null), 2000);
      alert.success('Copied to clipboard');
    } catch {
      alert.error('Copy failed');
    }
  };

  const handleDownload = (json, index) => {
    const text = JSON.stringify(json ?? null, null, 2);
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sample-${index + 1}.json`;
    link.click();
    URL.revokeObjectURL(url);
    alert.success('Download started');
  };

  // ── Build modal item from cache + loading state ───────────────────────────
  const getModalItem = (index) => {
    if (index == null || index >= urlRows.length) return null;
    const row = urlRows[index];
    const cached = jsonCache[row.s3Url];
    const isLoading = loadingUrls.has(row.s3Url);
    return {
      s3Url: row.s3Url,
      loading: isLoading && !cached,
      json: cached?.json ?? null,
      error: cached?.error ?? null,
    };
  };

  const modalItem = getModalItem(modalIndex);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <AppLayout>
      <div className="sjv-container">
        {globalLoading && (
          <div className="sjv-loader-wrap">
            <Loader />
          </div>
        )}

        {/* Header */}
        <div className="sjv-header">
          <h1 className="sjv-title">Get Client Sample JSON</h1>
          <p className="sjv-subtitle">
            Fetch sample S3 report URLs for any org/center, then open each JSON on demand.
          </p>
        </div>

        {/* Controls */}
        <div className="sjv-controls">
          <div className="sjv-control-row">
            <div className="sjv-field">
              <label className="sjv-label">Organization</label>
              <input
                type="text"
                className="sjv-input"
                value={org}
                onChange={(e) => setOrg(e.target.value)}
                placeholder="e.g. rajagiri"
              />
            </div>
            <div className="sjv-field">
              <label className="sjv-label">Center</label>
              <input
                type="text"
                className="sjv-input"
                value={center}
                onChange={(e) => setCenter(e.target.value)}
                placeholder="e.g. rajagiri"
              />
            </div>
            <div className="sjv-field">
              <label className="sjv-label">Count (1–{MAX_COUNT})</label>
              <input
                type="number"
                className="sjv-input sjv-input-num"
                min={MIN_COUNT}
                max={MAX_COUNT}
                value={count}
                onChange={(e) => setCount(e.target.value)}
              />
            </div>
          </div>
          <div className="sjv-fetch-row">
            <button
              type="button"
              className="sjv-btn-primary"
              onClick={fetchSamples}
              disabled={globalLoading}
            >
              <FaSyncAlt /> Fetch Samples
            </button>
          </div>
        </div>

        {/* Empty / error states */}
        {!globalLoading && urlRows.length === 0 && (error || hasFetched) && (
          <div className="sjv-negative">
            <div className="sjv-negative-card">
              <FaExclamationTriangle className="sjv-negative-icon" />
              <h2 className="sjv-negative-title">No response</h2>
              <p className="sjv-negative-message">
                {error || 'No sample data returned. Check organization and center, then try again.'}
              </p>
              <button type="button" className="sjv-btn-primary" onClick={fetchSamples}>
                <FaSyncAlt /> Try again
              </button>
            </div>
          </div>
        )}

        {/* ── Results table ── */}
        {urlRows.length > 0 && (
          <div className="sjv-results" ref={resultsRef}>
            <div className="sjv-results-header">
              <div className="sjv-results-title-row">
                <FaTable className="sjv-results-icon" />
                <h2 className="sjv-results-title">
                  {urlRows.length} sample{urlRows.length !== 1 ? 's' : ''} found
                </h2>
              </div>
              <p className="sjv-results-hint">
                Click <strong>View JSON</strong> on any row to open its full payload in a popup.
              </p>
            </div>

            <div className="sjv-table-wrap">
              <table className="sjv-table">
                <thead>
                  <tr>
                    <th className="sjv-th sjv-th-num">#</th>
                    <th className="sjv-th">File</th>
                    <th className="sjv-th sjv-th-status">Status</th>
                    <th className="sjv-th sjv-th-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {urlRows.map((row, index) => {
                    const cached = jsonCache[row.s3Url];
                    const isLoading = loadingUrls.has(row.s3Url);
                    const hasError = cached && cached.error;
                    const hasJson = cached && cached.json;

                    let statusBadge;
                    if (isLoading) {
                      statusBadge = <span className="sjv-badge sjv-badge-loading"><FaSpinner className="sjv-spin" /> Loading</span>;
                    } else if (hasError) {
                      statusBadge = <span className="sjv-badge sjv-badge-error">Error</span>;
                    } else if (hasJson) {
                      statusBadge = <span className="sjv-badge sjv-badge-ready">Ready</span>;
                    } else {
                      statusBadge = <span className="sjv-badge sjv-badge-idle">Not loaded</span>;
                    }

                    return (
                      <tr key={index} className="sjv-tr">
                        <td className="sjv-td sjv-td-num">{index + 1}</td>
                        <td className="sjv-td sjv-td-file">
                          <span className="sjv-filename">{row.filename}</span>
                          <span className="sjv-file-url" title={row.s3Url}>{row.s3Url}</span>
                        </td>
                        <td className="sjv-td sjv-td-status">{statusBadge}</td>
                        <td className="sjv-td sjv-td-action">
                          <button
                            type="button"
                            className="sjv-btn-view"
                            onClick={() => handleViewJson(index)}
                            title={`View JSON for sample ${index + 1}`}
                            disabled={isLoading && !cached}
                          >
                            <FaEye /> View JSON
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Initial empty state */}
        {!globalLoading && urlRows.length === 0 && !error && !hasFetched && (
          <div className="sjv-empty">
            <FaFileCode className="sjv-empty-icon" />
            <p>Set organization, center, and count, then click <strong>Fetch Samples</strong> to load.</p>
          </div>
        )}

        {/* ── JSON viewer modal ── */}
        {modalIndex != null && modalItem && (
          <JsonViewerModal
            item={modalItem}
            index={modalIndex}
            onClose={handleCloseModal}
            onCopy={handleCopy}
            onDownload={handleDownload}
            copySuccess={copySuccess}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default SampleJsonViewer;
